import type { ErrorEvent, StackFrame } from "@sentry/nextjs"

// Known external error patterns to ignore
const EXTERNAL_ERROR_PATTERNS = [
  // Browser extension errors
  /chrome-extension:/,
  /moz-extension:/,
  /safari-extension:/,
  /webkit-masked-url:/,

  // External script errors
  /Non-Error promise rejection captured/,
  /Non-Error exception captured/,

  // Browser API errors that are external
  /ResizeObserver loop limit exceeded/,
]

// External domains to ignore
const BLOCKED_FILENAMES = [
  "google-analytics.com",
  "googletagmanager.com",
  "facebook.com",
  "twitter.com",
  "x.com",
  "doubleclick.net",
  "googlesyndication.com",
  "segment.io",
  "amplitude.com",

  // metamask
  "inpage.js",
]

// Internal application domains and patterns
const INTERNAL_DOMAINS = ["opensea.io", "localhost", "127.0.0.1", "vercel.app"]

// Internal file patterns that indicate application code
const INTERNAL_FILE_PATTERNS = [
  // Source-mapped workspace paths (production with source maps)
  /\.\.\/packages\//,
  /\.\.\/apps\//,

  // Next.js application files
  /_next\/static\/chunks\//,
  /_next\/static\/pages\//,
  /\/pages\//,
  /\/app\//,
  /page-.+/,

  // Webpack bundles
  /webpack:\/\/\//,

  // Internal packages
  /@opensea\//,

  // Application source paths
  /\/src\//,
  /\/lib\//,
  /\/components\//,
  /\/utils\//,
]

/**
 * Checks if an error originates from our application code
 * Only returns true if we can positively identify the error as internal
 */
function isInternalError(event: ErrorEvent): boolean {
  const frames = event?.exception?.values?.[0]?.stacktrace?.frames || []

  // If no stack trace, it's likely external
  if (frames.length === 0) {
    return false
  }

  // Check if at least one frame is from our application
  return frames.some((frame: StackFrame) => {
    const filename = frame?.filename
    if (!filename) {
      return false
    }

    // First check if it's explicitly blocked (external)
    const isBlockedFilename = BLOCKED_FILENAMES.some(domain =>
      filename.includes(domain),
    )
    if (isBlockedFilename) {
      return false
    }

    // Check if filename contains internal domain
    const hasInternalDomain = INTERNAL_DOMAINS.some(domain =>
      filename.includes(domain),
    )
    if (hasInternalDomain) {
      return true
    }

    // Check if filename matches internal file patterns
    return INTERNAL_FILE_PATTERNS.some(pattern => pattern.test(filename))
  })
}

/**
 * Checks if error message matches external error patterns
 */
function hasExternalErrorPattern(event: ErrorEvent): boolean {
  const errorMessage =
    event?.exception?.values?.[0]?.value || event?.message || ""

  return EXTERNAL_ERROR_PATTERNS.some(pattern => pattern.test(errorMessage))
}

/**
 * Checks if error is from a browser extension
 */
function isBrowserExtensionError(event: ErrorEvent): boolean {
  const frames = event?.exception?.values?.[0]?.stacktrace?.frames || []

  return frames.some((frame: StackFrame) => {
    const filename = frame?.filename || ""
    return (
      filename.includes("chrome-extension://") ||
      filename.includes("moz-extension://") ||
      filename.includes("safari-extension://") ||
      filename.includes("webkit-masked-url:")
    )
  })
}

export function shouldSendErrorToSentry(event: ErrorEvent): boolean {
  // Skip if it's a browser extension error
  if (isBrowserExtensionError(event)) {
    return false
  }

  // Skip if it matches external error patterns
  if (hasExternalErrorPattern(event)) {
    return false
  }

  // Skip if it's not from our application code
  if (!isInternalError(event)) {
    return false
  }

  return true
}
